/**
 * The mention of a document whose security elements no certified MCF
 * produced, printed on invoices and tickets (`nonCertifiedMention`) and on
 * the X, Z and A reports (`nonCertifiedReportMention`). It describes the
 * document, never the software's homologation. It speaks of the device only:
 * the virtual MCF backs real sales, so nothing here may assert why the
 * document was issued.
 */
import { FISCAL_MODALITIES } from "./certification-obligation.js";
/** Headline of the mention on an invoice, a ticket and a report covering no certified document. */
export const NON_CERTIFIED_TITLE = "DOCUMENT NON CERTIFIÉ — SANS VALEUR FISCALE";
/** Headline on a report that also covers a document of modality MCF_CERTIFIE. */
const PARTLY_NON_CERTIFIED_REPORT_TITLE = "RAPPORT PORTANT EN PARTIE SUR DES ÉLÉMENTS NON CERTIFIÉS";
/** What the security elements printed on an invoice or a ticket are worth. */
export const NON_CERTIFIED_BODY = "Les éléments de sécurité portés sur ce document (NIM, compteur, " +
    "signature, code QR) ne proviennent pas d'un module de contrôle de " +
    "facturation (MCF) certifié.";
/**
 * What a report is worth. A report carries no security element of its own and
 * may cover certified documents beside the others, so it speaks of what it
 * covers.
 */
const NON_CERTIFIED_REPORT_BODY = "Ce rapport porte, en tout ou partie, sur des documents ou une clôture dont " +
    "les éléments de sécurité ne proviennent pas d'un module de contrôle de " +
    "facturation (MCF) certifié.";
/** Plain-French name of each non-certified signing device. */
const DEVICE_LABELS = {
    [FISCAL_MODALITIES.MCF_VIRTUEL]: "MCF virtuel",
    [FISCAL_MODALITIES.SIMULATEUR]: "simulateur",
    [FISCAL_MODALITIES.INCONNU]: "dispositif inconnu",
};
/**
 * Whether the mention applies. NULL and undefined print nothing: a document
 * recording no device asserts neither certification nor its absence.
 */
export function isNonCertified(fiscalModality) {
    if (fiscalModality === null || fiscalModality === undefined)
        return false;
    return fiscalModality !== FISCAL_MODALITIES.MCF_CERTIFIE;
}
function mentionWith(body, fiscalModality) {
    if (!isNonCertified(fiscalModality))
        return null;
    const label = DEVICE_LABELS[fiscalModality] ?? DEVICE_LABELS[FISCAL_MODALITIES.INCONNU];
    return `${body} Dispositif de signature : ${label}.`;
}
/** Full mention of an invoice or a ticket, device named; null when nothing is to be printed. */
export function nonCertifiedMention(fiscalModality) {
    return mentionWith(NON_CERTIFIED_BODY, fiscalModality);
}
/** Full mention of a report, device named; null when nothing is to be printed. */
export function nonCertifiedReportMention(fiscalModality) {
    return mentionWith(NON_CERTIFIED_REPORT_BODY, fiscalModality);
}
/**
 * The device a report names, from the modalities of what it covers: null when
 * none is non-certified, the device when they name one, INCONNU when they name
 * several or a value outside the catalogue.
 */
export function nonCertifiedReportModality(modalities) {
    const devices = new Set();
    for (const modality of modalities) {
        if (isNonCertified(modality))
            devices.add(modality);
    }
    if (devices.size === 0)
        return null;
    const [only] = devices;
    return devices.size === 1 && only in DEVICE_LABELS ? only : FISCAL_MODALITIES.INCONNU;
}
/** The marking of a report over `modalities`: those of its documents, and of the device closing it. */
export function nonCertifiedReportMarking(modalities) {
    const all = [...modalities];
    const device = nonCertifiedReportModality(all);
    if (device === null)
        return {};
    return {
        nonCertifiedModality: device,
        ...(all.includes(FISCAL_MODALITIES.MCF_CERTIFIE) ? { coversCertifiedDocument: true } : {}),
    };
}
/** Headline of a report's mention, from its marking. */
export function nonCertifiedReportTitle(marking) {
    return marking.coversCertifiedDocument ? PARTLY_NON_CERTIFIED_REPORT_TITLE : NON_CERTIFIED_TITLE;
}
//# sourceMappingURL=non-certified-mention.js.map